import {
  Badge,
  BodyText,
  Heading,
  Icon,
  IconSet,
} from '@postscript/components';
import { Flex } from 'components/layout';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { StepBadge } from 'providers/ProgressiveOnboarding/types';

import classes from 'components/ProgressiveOnboarding/WorkflowStep.module.css';

interface Props {
  /** Shown in both expanded and collapsed states */
  title: string;

  /** Shown in the expanded state */
  content: React.ReactNode;

  /** Shown to left of title */
  icon?: keyof typeof IconSet;

  /** Rendered as an unordered list in the expanded state */
  subSteps?: string[];

  /** Primary action rendered at the bottom */
  primaryAction?: React.ReactNode;

  /** Secondary action rendered at the bottom */
  secondaryAction?: React.ReactNode;

  /** Completed steps are green. Incomplete steps can be completed. Blocked steps are disabled and de-emphasized. */
  state: 'complete' | 'incomplete' | 'blocked';

  /** Expanded/Collapsed state - managed by parent */
  isOpen?: boolean;

  /** Callback function to toggle between expanded/collapsed state */
  toggleStep?: () => void;

  /** Node above the step */
  nodeAbove?: React.ReactNode;

  /** Tooltip to show when hovering over the step */
  tooltip?: React.ReactNode;

  /** ID for the tooltip, used for aria-describedby and data-for attributes */
  tooltipId?: string;

  /** Optional badge to show next to the title */
  badge?: StepBadge;
}

export const WorkflowStep = ({
  title,
  content,
  icon,
  subSteps,
  primaryAction,
  secondaryAction,
  state,
  isOpen,
  toggleStep,
  nodeAbove,
  tooltip,
  tooltipId,
  badge,
}: Props) => {
  const isReducedMotion = useReducedMotion();

  const StepIcon = () => {
    if (state === 'complete') {
      return <Icon component={IconSet.CheckCircleFilled} size="medium" />;
    }

    if (icon) {
      return <Icon component={IconSet[icon]} size="medium" />;
    }

    return <Icon component={IconSet.Disc} size="medium" />;
  };

  // Only show tooltip when step is blocked
  const shouldShowTooltip = state === 'blocked' && tooltip && tooltipId;

  return (
    <>
      {nodeAbove}
      {shouldShowTooltip && tooltip}

      <div
        className={classes.workflowStep}
        aria-describedby={shouldShowTooltip ? tooltipId : undefined}
        data-for={shouldShowTooltip ? tooltipId : undefined}
        data-tip={shouldShowTooltip ? 'true' : undefined}
      >
        <button
          type="button"
          className={classes.header}
          onClick={toggleStep}
          disabled={state === 'blocked'}
          data-expanded={isOpen ? 'true' : undefined}
          data-state={state}
        >
          <StepIcon />

          <Heading size="xx-small" color="inherit">
            {title}
          </Heading>

          {badge && (
            <Badge variant={badge.variant ?? 'info'}>{badge.text}</Badge>
          )}

          {state !== 'blocked' && (
            <Icon
              component={IconSet[isOpen ? 'ChevronUp' : 'ChevronDown']}
              size="large"
              color="var(--text-color)"
            />
          )}
        </button>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              className={classes.content}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                duration: isReducedMotion ? 0 : 0.3,
                ease: 'easeInOut',
              }}
            >
              <BodyText size="medium">{content}</BodyText>

              {subSteps && (
                <ul>
                  {subSteps.map((task) => (
                    <li key={task}>
                      <BodyText size="medium">{task}</BodyText>
                    </li>
                  ))}
                </ul>
              )}

              <Flex gap="var(--spacing-2)">
                {primaryAction}
                {secondaryAction}
              </Flex>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};
